import { useState, useEffect, useMemo, useRef } from 'preact/hooks'
import { Upload, Loader2, Check } from 'lucide-preact'
import { TypeAheadDropdown } from '@/components/common/TypeAheadDropdown'
import type { TypeAheadOption } from '@/components/common/TypeAheadDropdown'
import { landingPagesApi } from '@/api/landingPages'
import type { FontCatalogEntryDto } from '@/types/landingPage'
import { useToast } from '@/hooks/useToast'
import { takeFontFile } from '@/utils/fontFile'

interface FontsSectionProps {
    workspaceId: string
    webId: string
    /** Current effective heading font family (per-page override or company brand font). */
    headingFamily?: string
    /** Current effective body font family. */
    bodyFamily?: string
    /** Locked while another user holds the chat turn, or mid-generation. */
    disabled?: boolean
    /**
     * Applies the chosen heading/body families. A null family clears that role back to
     * the system-font fallback. Pre-generation the parent stores it as the override that
     * rides into the Generate request; post-generation it re-bakes the draft instantly.
     */
    onApply: (heading: string | null, body: string | null) => void | Promise<void>
    /** Optional (post-generation): ask Webster to tune type scale/spacing for the current fonts. */
    onRefine?: () => void
    /** Set while the last build failed on a font nobody can load: the section leads with the two fixes. */
    recovery?: MissingFontRecovery
}

export interface MissingFontRecovery {
    family: string
    fromBrandGuidelines: boolean
    /** What fixed it, once fixed: the family itself after an upload, or the font chosen instead. */
    resolvedWith: string | null
    canEdit: boolean
    /** Bumped when the person asks to choose a font, to put the picker in focus. */
    focusRequest: number
    /** Registers the file under `family`, whatever it is called. Resolves true when it worked. */
    onUpload: (file: File) => Promise<boolean>
    /** Uses `replacement` for headings and body on this page only. Resolves true when it worked. */
    onReplace: (replacement: string) => Promise<boolean>
}


const UPLOAD_BTN_CLASS =
    'flex items-center justify-center gap-2 w-full px-3 py-2 text-sm border-2 border-dashed rounded-lg transition-colors text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 disabled:opacity-50 disabled:pointer-events-none'

export function FontsSection({ workspaceId, webId, headingFamily, bodyFamily, disabled: disabledByParent, onApply, onRefine, recovery }: FontsSectionProps) {
    // Every control here calls an IsWorkspaceEditor endpoint; on the failure screen a viewer is told who to ask instead.
    const disabled = disabledByParent || (recovery ? !recovery.canEdit : false)
    const { showError } = useToast()
    const [catalog, setCatalog] = useState<FontCatalogEntryDto[]>([])
    const [customFamilies, setCustomFamilies] = useState<string[]>([])
    const [headingSearch, setHeadingSearch] = useState('')
    const [bodySearch, setBodySearch] = useState('')
    const [uploading, setUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        let active = true
        landingPagesApi.getFontCatalog().then((res) => {
            if (active && res.data) setCatalog(res.data)
        })
        return () => {
            active = false
        }
    }, [])

    // Uploaded custom fonts appear first, then the curated catalog.
    const allFamilies = useMemo<FontCatalogEntryDto[]>(() => {
        // Uploaded fonts first, then the catalog, de-duped by family (case-insensitive)
        // so the picker never renders duplicate option keys.
        const seen = new Set<string>()
        const result: FontCatalogEntryDto[] = []
        for (const entry of [...customFamilies.map((f) => ({ Family: f, Category: 'Uploaded' })), ...catalog]) {
            const key = entry.Family.trim().toLowerCase()
            if (key && !seen.has(key)) {
                seen.add(key)
                result.push(entry)
            }
        }
        return result
    }, [catalog, customFamilies])

    const toOptions = (search: string): TypeAheadOption<FontCatalogEntryDto>[] => {
        const term = search.trim().toLowerCase()
        return allFamilies
            .filter((f) => !term || f.Family.toLowerCase().includes(term))
            .slice(0, 50)
            .map((f) => ({
                key: f.Family,
                label: f.Family,
                data: f,
                render: () => (
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-900 dark:text-white">{f.Family}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{f.Category}</span>
                    </div>
                ),
            }))
    }

    const handleUpload = async (e: Event) => {
        const file = takeFontFile(e, showError)
        if (!file) return

        setUploading(true)
        const family = file.name.replace(/\.woff2$/i, '')
        const res = await landingPagesApi.uploadFont(workspaceId, webId, family, file)
        setUploading(false)

        if (res.data) {
            const uploadedFamily = res.data.Family
            setCustomFamilies((prev) => (prev.includes(uploadedFamily) ? prev : [uploadedFamily, ...prev]))
            // Default a just-uploaded custom font to the body role.
            await onApply(headingFamily ?? null, uploadedFamily)
        } else {
            showError('Upload failed', res.error || 'Could not upload the font.')
        }
    }

    return (
        <div className="space-y-3">
            {recovery && <RecoveryBlock recovery={recovery} disabled={disabled} toOptions={toOptions} />}
            <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Heading font</label>
                <TypeAheadDropdown<FontCatalogEntryDto>
                    minSearchLength={0}
                    disabled={disabled}
                    placeholder={headingFamily || 'System default'}
                    searchValue={headingSearch}
                    onSearchChange={setHeadingSearch}
                    options={toOptions(headingSearch)}
                    onSelect={(opt) => {
                        setHeadingSearch('')
                        onApply(opt.data.Family, bodyFamily ?? null)
                    }}
                    showClearButton
                    onClear={() => onApply(null, bodyFamily ?? null)}
                    noResultsMessage="No matching fonts"
                />
            </div>

            <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Body font</label>
                <TypeAheadDropdown<FontCatalogEntryDto>
                    minSearchLength={0}
                    disabled={disabled}
                    placeholder={bodyFamily || 'System default'}
                    searchValue={bodySearch}
                    onSearchChange={setBodySearch}
                    options={toOptions(bodySearch)}
                    onSelect={(opt) => {
                        setBodySearch('')
                        onApply(headingFamily ?? null, opt.data.Family)
                    }}
                    showClearButton
                    onClear={() => onApply(headingFamily ?? null, null)}
                    noResultsMessage="No matching fonts"
                />
            </div>

            <div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".woff2,font/woff2"
                    className="hidden"
                    onChange={handleUpload}
                />
                <button
                    type="button"
                    disabled={disabled || uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className={UPLOAD_BTN_CLASS}
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? 'Uploading…' : 'Upload a custom font (.woff2)'}
                </button>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    Google Fonts load automatically; upload a .woff2 for a custom or licensed brand font.
                </p>
            </div>

            {onRefine && (
                <div className="pt-1">
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onRefine}
                        className="w-full text-left text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-50"
                    >
                        Ask Webster to fine-tune the type scale & spacing →
                    </button>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                        Sends a message to Webster chat and switches you there so you can watch it work.
                    </p>
                </div>
            )}
        </div>
    )
}

interface RecoveryBlockProps {
    recovery: MissingFontRecovery
    disabled?: boolean
    toOptions: (search: string) => TypeAheadOption<FontCatalogEntryDto>[]
}

/**
 * The two ways out of a build that stopped on a font: supply the font, or choose another for this
 * page only. Brand Guidelines are never changed from here.
 */
function RecoveryBlock({ recovery, disabled, toOptions }: RecoveryBlockProps) {
    const { showError } = useToast()
    const { family, fromBrandGuidelines, resolvedWith, canEdit, focusRequest } = recovery
    const [search, setSearch] = useState('')
    const [choice, setChoice] = useState<string | null>(null)
    const [working, setWorking] = useState<'upload' | 'replace' | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const pickerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (focusRequest === 0) return
        // The same request opens Typography; focus waits a frame for the panel to stop being hidden.
        const frame = requestAnimationFrame(() => pickerRef.current?.querySelector('input')?.focus())
        return () => cancelAnimationFrame(frame)
    }, [focusRequest])

    if (resolvedWith) {
        return (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                <span>{resolvedWith === family
                    ? `${family} is uploaded for this page.`
                    : `This page uses ${resolvedWith} instead of ${family}.`}</span>
            </div>
        )
    }

    const source = fromBrandGuidelines
        ? `${family} is the brand font in your Brand Guidelines.`
        : `${family} is the font chosen for this page.`
    if (!canEdit) {
        return (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-200">
                {source} It has no font file, so the page can't be built yet. Ask a workspace editor to upload {family} or choose a font for this page.
            </div>
        )
    }

    const upload = async (e: Event) => {
        const file = takeFontFile(e, showError)
        if (!file) return
        setWorking('upload')
        await recovery.onUpload(file)
        setWorking(null)
    }

    const replace = async () => {
        if (!choice) return
        setWorking('replace')
        await recovery.onReplace(choice)
        setWorking(null)
    }

    return (
        <div className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-3">
            <p className="text-sm text-amber-900 dark:text-amber-200">
                {source} It isn't a Google Font and has no font file, so the page can't be built yet.
            </p>
            <div ref={pickerRef}>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Use instead of {family} on this page</label>
                <TypeAheadDropdown<FontCatalogEntryDto>
                    minSearchLength={0}
                    disabled={disabled || working !== null}
                    placeholder={choice ?? 'Choose a Google Font'}
                    searchValue={search}
                    onSearchChange={setSearch}
                    options={toOptions(search).filter(option => option.data.Family.toLowerCase() !== family.toLowerCase())}
                    onSelect={(option) => {
                        setSearch('')
                        setChoice(option.data.Family)
                    }}
                    noResultsMessage="No matching fonts"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Applies to headings and body.{fromBrandGuidelines ? ` Your Brand Guidelines still say ${family}, and other pages keep using it.` : ''}
                </p>
                <button
                    type="button"
                    disabled={!choice || disabled || working !== null}
                    onClick={replace}
                    className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                    {working === 'replace' && <Loader2 className="w-4 h-4 animate-spin" />}
                    {choice ? `Use ${choice}` : 'Use this font'}
                </button>
            </div>
            <div className="border-t border-amber-200 dark:border-amber-800/60 pt-3">
                <input ref={fileInputRef} type="file" accept=".woff2,font/woff2" className="hidden" onChange={upload} />
                <button
                    type="button"
                    disabled={disabled || working !== null}
                    onClick={() => fileInputRef.current?.click()}
                    className={UPLOAD_BTN_CLASS}
                >
                    {working === 'upload' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {working === 'upload' ? 'Uploading…' : `Upload ${family} (.woff2)`}
                </button>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    If you have the licensed file, upload it to stay on brand. It's saved as {family} for this page, whatever the file is called. Up to 2 MB.
                </p>
            </div>
        </div>
    )
}
